import { createContext, useContext, useState, useCallback } from "react";

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      type: toast.type || "info",
      message: toast.message,
      title: toast.title,
      duration: toast.duration || 3000,
      autoClose: toast.autoClose !== false,
    };
    
    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback((message, options = {}) => {
    return showToast({ ...options, type: "success", message });
  }, [showToast]);

  const error = useCallback((message, options = {}) => {
    return showToast({ ...options, type: "error", message });
  }, [showToast]);

  const warning = useCallback((message, options = {}) => {
    return showToast({ ...options, type: "warning", message });
  }, [showToast]);

  const info = useCallback((message, options = {}) => {
    return showToast({ ...options, type: "info", message });
  }, [showToast]);

  return (
    <ToastContext.Provider
      value={{
        toasts,
        showToast,
        removeToast,
        success,
        error,
        warning,
        info,
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};

